import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { environment as env } from '../../environments/environment';
import { errorWithSupportId } from 'f2tech-shared/support-toast';
import { reportError, type ErrorDiagnostics } from 'f2tech-shared/error-reports';

/**
 * Thin wrapper over ngx-toastr that mints a Support ID for every
 * .error() call. The placeholder→reportError→swap→onTap-copy flow lives
 * in f2tech-shared/support-toast so every SPA stays in lockstep, and
 * f2-admin-service's ErrorReportsService captures the client IP
 * server-side so the support viewer surfaces full diagnostics with
 * location context.
 *
 * IMPORTANT: a baseline diagnostics object is supplied on every error
 * so the helper always takes the report path. Without it, calls that
 * pass only (title, message) short-circuit -- the toast renders but
 * /admin/error-reports never sees the entry, which was the bug Mike
 * surfaced with the "Failed to create user … SES quota" toast on
 * admin.f2-tech.ai. Caller-supplied diagnostics still win on overlap.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {

  constructor(private toastr: ToastrService) { }

  success(title: string, message?: string) {
    this.toastr.success(message, title);
  }

  info(title: string, message?: string) {
    this.toastr.info(message, title);
  }

  warning(title: string, message?: string) {
    this.toastr.warning(message, title);
  }

  error(title: string, message?: string, diagnostics?: ErrorDiagnostics) {
    // IT-F2-421 (Mike 2026-09-23 122-ER hygiene sweep) —
    //   1. Drop empty ERs. If the caller passed no message + no
    //      diagnostics-beyond-defaults, don't POST to /error-reports
    //      at all — just show a plain toast. Prior behavior lit up
    //      /admin/error-reports with rows that had "" message + null
    //      scanner_path + no error_code, useless to support.
    //   2. Default error_code when the caller doesn't supply one, so
    //      every row has SOMETHING searchable. Derive from
    //      window.location.pathname (e.g. /admin/users →
    //      F2ADMIN_USERS_ERROR) so rows still group by SPA surface
    //      even without a caller-specific tag.
    const trimmedMsg = (message || '').trim();
    const hasCallerContext = !!(diagnostics && Object.keys(diagnostics).some((k) => {
      const v = (diagnostics as any)[k];
      return v !== undefined && v !== null && v !== '';
    }));
    if (!trimmedMsg && !hasCallerContext) {
      this.toastr.error(message, title);
      return;
    }
    const pathname = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const derivedCode = this._deriveErrorCode(pathname);
    const merged: ErrorDiagnostics = {
      customer: 'f2-admin',
      scanner_path: pathname,
      f2_response_message: message,
      error_code: derivedCode,
      ...(diagnostics || {}),
    };
    // f2tech-shared's reportError() short-circuits when apiBase is
    // falsy — but f2-admin's env.apiUrl is intentionally empty (Vercel
    // rewrites `/rest/*` to f2-admin-service2 at the edge for same-
    // origin CORS bypass, per environment.ts). Without the fallback
    // below, ZERO ERs get submitted from admin.f2-tech.ai — the toast
    // fires but /admin/error-reports never sees it, which is what Mike
    // hit trying to diagnose the F2A_SYMBOL_LISTS_LIST_FAIL 404 toast.
    // Fall back to window.location.origin so the POST goes to the same
    // origin as the SPA and rides the same rewrite.
    const apiBase = env.apiUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    errorWithSupportId(
      this.toastr as any,
      title,
      message,
      merged,
      apiBase,
    );
  }

  /**
   * Derive a stable error_code from the current URL pathname so /admin/
   * error-reports rows are groupable by SPA surface even when the caller
   * didn't supply a specific code. Trims trailing IDs / slugs (e.g.
   * /admin/customer-directory/oxc → F2ADMIN_CUSTOMER_DIRECTORY_ERROR).
   */
  private _deriveErrorCode(pathname?: string): string {
    if (!pathname) return 'F2ADMIN_UNTAGGED_ERROR';
    const segs = pathname.split('/').filter(Boolean);
    if (segs.length === 0) return 'F2ADMIN_ROOT_ERROR';
    const meaningful = segs
      .filter((s) => s !== 'admin')
      .filter((s) => !/^[0-9a-f-]{20,}$/i.test(s) && !/^\d+$/.test(s))
      .slice(0, 2);
    if (meaningful.length === 0) return 'F2ADMIN_ADMIN_ERROR';
    const code = meaningful
      .join('_')
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .toUpperCase();
    return `F2ADMIN_${code}_ERROR`;
  }

  /**
   * Non-error events that should STILL be supportable -- e.g. reconcile
   * completion summaries, bulk-delete results. Submits the same shape
   * to /admin/error-reports so support can lookup by Support ID, and
   * renders a green success toast with that ID appended + click to copy.
   * Strictly additive -- doesn't change existing success() callers.
   */
  async successWithSupportId(
    title: string,
    message?: string,
    diagnostics?: ErrorDiagnostics,
  ): Promise<void> {
    const merged: ErrorDiagnostics = {
      customer: 'f2-admin',
      scanner_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      f2_response_message: message,
      error_code: diagnostics?.error_code || 'F2ADMIN_INFO_E01',
      ...(diagnostics || {}),
    };
    const apiBase = env.apiUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    try {
      const res: any = await reportError(merged, apiBase);
      const supportId: string | undefined = (res && 'id' in res) ? res.id : undefined;
      const body = supportId
        ? `${message || ''}\n\nSupport ID: ${supportId} (click to copy)`
        : (message || '');
      const t: any = this.toastr.success(body, title, {
        timeOut: 0, extendedTimeOut: 0, tapToDismiss: false, closeButton: true,
      });
      if (supportId && t?.onTap) {
        t.onTap.subscribe(() => {
          if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(supportId).catch(() => {});
          }
        });
      }
    } catch {
      // Fall back to a plain success toast if the report submit fails;
      // the user still sees the outcome, just without an ID.
      this.toastr.success(message, title);
    }
  }
}
